import { useState, useRef, useEffect } from 'react';
import { Folder as FolderIcon } from '@/components/icons';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import { DiagramIconPicker } from '../DiagramIconPicker';
import { DEFAULT_DIAGRAM_ICON, DEFAULT_DIAGRAM_ICON_COLOR } from '../diagramIcons';

interface EditFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, data: { name: string; color: string; icon: string }) => Promise<void>;
  folder: { id: string; name: string; color?: string | null; icon?: string | null } | null;
}

export default function EditFolderModal({ isOpen, onClose, onSave, folder }: EditFolderModalProps) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(DEFAULT_DIAGRAM_ICON);
  const [color, setColor] = useState(DEFAULT_DIAGRAM_ICON_COLOR);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && folder) {
      setName(folder.name);
      setIcon(folder.icon || DEFAULT_DIAGRAM_ICON);
      setColor(folder.color || DEFAULT_DIAGRAM_ICON_COLOR);
      setIsSaving(false);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 60);
    }
  }, [isOpen, folder]);

  const handleSave = async () => {
    if (!name.trim() || !folder) return;
    setIsSaving(true);
    try {
      await onSave(folder.id, { name: name.trim(), color, icon });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen && !!folder} onOpenChange={(open) => { if (!open && !isSaving) onClose(); }}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        <div className="flex items-center gap-3 border-b border-border px-6 py-4">
          <div className="rounded-lg bg-primary/10 p-2">
            <FolderIcon className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-lg font-semibold text-foreground">Edit folder</DialogTitle>
        </div>

        <div className="p-6">
          <label className="mb-2 block text-sm font-medium text-foreground dark:text-muted-foreground">
            Icon &amp; name
          </label>
          <div className="flex items-center gap-2">
            <DiagramIconPicker
              icon={icon}
              color={color}
              onChange={(i, c) => { setIcon(i); setColor(c); }}
              triggerClassName="h-10 w-10 flex-none"
            />
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder="Folder name"
              disabled={isSaving}
              className="w-full flex-1 rounded-lg border border-border bg-card px-3 py-2 text-foreground placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-muted px-6 py-4 dark:bg-card/50">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg bg-muted px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/70 disabled:opacity-50 dark:text-muted-foreground"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving && (
              <svg className="h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Save changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
